import { createElement } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
} from 'react-native';
import type { DemoAccountKey } from '@perdiem/trpc/demo-accounts';
import { ResponsiveModal } from './ResponsiveModal';

export const DEMO_PROFILE_PICKER_COPY = {
  eyebrow: 'Demo profiles',
  headline: 'Look around before signing up.',
  description: 'Open a read-only household with example accounts, budgets, transactions, and a completed FIRE plan.',
  ctaTitle: 'Look around before signing up.',
  ctaDescription: 'Open a read-only demo household with example spending, accounts, and a finished FIRE plan.',
} as const;

const DEMO_CARD_ACCENTS: Record<string, string> = {
  alreadyfi: 'bg-emerald-400/10 text-emerald-300',
  fire: 'bg-forest-500/10 text-forest-500',
  fatfire: 'bg-amber-400/10 text-amber-300',
  baristafire: 'bg-sky-400/10 text-sky-300',
  coastfire: 'bg-rose-400/10 text-rose-300',
  gregfire: 'bg-lime-400/10 text-lime-300',
};

type DemoContextVideo = {
  title: string;
  embedUrl: string;
};

export type DemoProfilePickerAccount = {
  key: DemoAccountKey;
  label: string;
  title: string;
  summary: string;
  household: string;
  ageLabel: string;
  location: string;
  contextVideos?: readonly DemoContextVideo[];
};

type DemoProfilePickerModalProps = {
  visible: boolean;
  accounts: readonly DemoProfilePickerAccount[];
  launchingDemoKey: DemoAccountKey | null;
  onClose: () => void;
  onLaunchDemo: (accountKey: DemoAccountKey) => void;
};

function DemoContextVideoCard({ video }: { video: DemoContextVideo }) {
  if (Platform.OS !== 'web') return null;

  return (
    <View
      className="overflow-hidden rounded-2xl border border-stone-800 bg-black lg:flex-1"
      style={{ aspectRatio: 16 / 9, minHeight: 180 }}
    >
      {createElement('iframe', {
        src: video.embedUrl,
        title: video.title,
        allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
        allowFullScreen: true,
        loading: 'lazy',
        referrerPolicy: 'strict-origin-when-cross-origin',
        style: {
          border: 0,
          display: 'block',
          height: '100%',
          width: '100%',
        },
      })}
    </View>
  );
}

export function DemoProfilePickerModal({
  visible,
  accounts,
  launchingDemoKey,
  onClose,
  onLaunchDemo,
}: DemoProfilePickerModalProps) {
  return (
    <ResponsiveModal
      visible={visible}
      onClose={onClose}
      maxWidth={920}
      maxHeight={760}
      androidMinHeight={520}
      shellClassName="border border-stone-800 bg-stone-950 dark:bg-stone-950"
      header={
        <View>
          <Text className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">
            {DEMO_PROFILE_PICKER_COPY.eyebrow}
          </Text>
          <Text className="mt-3 text-2xl font-semibold text-stone-50">
            {DEMO_PROFILE_PICKER_COPY.headline}
          </Text>
        </View>
      }
      subtitle={
        <Text className="max-w-3xl text-sm leading-6 text-stone-300">
          {DEMO_PROFILE_PICKER_COPY.description}
        </Text>
      }
    >
      <ScrollView
        className="flex-1 px-5 pb-5"
        contentContainerStyle={{ gap: 16, paddingBottom: 24 }}
      >
        {accounts.map((account) => {
          const isLaunching = launchingDemoKey === account.key;
          const contextVideos = account.contextVideos ?? null;

          return (
            <View
              key={account.key}
              className="rounded-[24px] border border-stone-800 bg-stone-900/80 p-5"
            >
              <View className="flex-row flex-wrap items-start justify-between gap-3">
                <View className="max-w-2xl flex-1">
                  <Text
                    className={`self-start rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] ${
                      DEMO_CARD_ACCENTS[account.key] ?? 'bg-emerald-500/10 text-emerald-300'
                    }`}
                  >
                    {account.label}
                  </Text>
                  <Text className="mt-3 text-xl font-semibold text-stone-50">
                    {account.title}
                  </Text>
                  <Text className="mt-2 text-sm leading-6 text-stone-300">
                    {account.summary}
                  </Text>
                  <Text className="mt-3 text-xs uppercase tracking-[0.16em] text-stone-500">
                    {account.household} · {account.ageLabel} · {account.location}
                  </Text>
                </View>

                <Pressable
                  onPress={() => onLaunchDemo(account.key)}
                  className={`rounded-2xl px-4 py-3 ${isLaunching ? 'bg-stone-700' : 'bg-forest-500'}`}
                  disabled={isLaunching}
                >
                  {isLaunching ? (
                    <ActivityIndicator color="#f5f5f4" />
                  ) : (
                    <Text className="text-sm font-semibold text-stone-950">Open demo</Text>
                  )}
                </Pressable>
              </View>

              {contextVideos && contextVideos.length > 0 ? (
                <View className="mt-5">
                  <Text className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                    Source material
                  </Text>
                  <View className="mt-3 gap-3 lg:flex-row">
                    {contextVideos.map((video) => (
                      <DemoContextVideoCard key={video.embedUrl} video={video} />
                    ))}
                  </View>
                </View>
              ) : null}
            </View>
          );
        })}
      </ScrollView>
    </ResponsiveModal>
  );
}
